import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { getHrisSyncPayrollData } from './hris-sync-payroll-api'
import { PayrollSelectedStatusType } from './types'
import { getMyClients } from 'enrollment-platform/api/persons'
import { CompanyListType } from '../types'

function usePayrollApiActivity() {
  const [selectedStatus, setSelectedStatus] =
    useState<PayrollSelectedStatusType>('')
  const [selectedClientId, setSelectedClientId] = useState<string>('')
  const [pagination, setPagination] = useState(1)
  const [companyList, setCompanyList] = useState<CompanyListType[]>([])

  useEffect(() => {
    const getClients = async () => {
      const customers: any[] = await getMyClients()
      const customersPayrollEnabled = customers
        .filter((c) => c.payrollConfiguration.isEnabled)
        .map((c) => ({ id: c.id, companyName: c.name }))
      setCompanyList(customersPayrollEnabled)
    }
    getClients()
  }, [])

  const { isPending, error, data, isError, refetch } = useQuery({
    queryKey: ['census', pagination, selectedClientId, selectedStatus],
    queryFn: () =>
      getHrisSyncPayrollData({
        page: pagination,
        limit: 50,
        clientPublicKey: selectedClientId || undefined,
        status: selectedStatus || undefined,
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })

  const handleStatusChange = useCallback((value: PayrollSelectedStatusType) => {
    setSelectedStatus(value)
  }, [])

  const handleCompanyChange = useCallback(
    (value: string) => {
      const selectedCompany = companyList.find((c) => c.id === value)
      setSelectedClientId(selectedCompany?.id || '')
      setPagination(1) // Reset to page 1
    },
    [companyList]
  )

  const handlePaginationChange = useCallback((page: number) => {
    setPagination(page)
  }, [])

  return {
    selectedStatus,
    selectedClientId,
    handleStatusChange,
    handleCompanyChange,
    handlePaginationChange,
    pagination,
    isPending,
    error,
    data,
    isError,
    refetch,
    companyList,
  }
}

export default usePayrollApiActivity
